import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {TRANSLATION_KEYS} from 'locale';

import {ActivityFilterType} from '../enums';
import type {ActivityFilters} from '../types';

import {PreSessionFlowFunnelChart} from './PreSessionFlowFunnelChart';
import {PreSessionFlowOverview} from './PreSessionFlowOverview';
import {PreSessionFlowRunDetail} from './PreSessionFlowRunDetail';
import {PreSessionFlowTimePerStage} from './PreSessionFlowTimePerStage';
import {PreSessionFlowValidationFailures} from './PreSessionFlowValidationFailures';
import {preSessionFlowAnalyticsApi} from './api';
import type {
    AnalyticsFilters, FunnelResult, OverviewResult, TimePerStageResult,
    ValidationFailureResult, VersionLabel,
} from './types';

const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY.PRE_SESSION_FLOW;

const AVAILABLE_FILTERS = [
    ActivityFilterType.StartDate,
    ActivityFilterType.EndDate,
    ActivityFilterType.Group,
    ActivityFilterType.Subgroup,
    ActivityFilterType.SessionId,
    ActivityFilterType.UserInfoId,
];

type Props = {
    filters: ActivityFilters | null;
    isLoading: boolean;
    setAvailableFilters: (filters: ActivityFilterType[]) => void;
    onToggleLoading: (value: boolean) => void;
};

const toAnalyticsFilters = (filters: ActivityFilters | null): AnalyticsFilters | null => {
    if (!filters?.startDate || !filters?.endDate) {
        return null;
    }
    return {
        startDate: filters.startDate.toDate(),
        endDate: filters.endDate.toDate(),
        groupId: filters.groupId,
        subgroupId: filters.subgroupId,
        sessionId: filters.sessionId || undefined,
        userInfoId: filters.userInfoId || undefined,
    };
};

export const PreSessionFlow = ({filters, isLoading: parentLoading, setAvailableFilters, onToggleLoading}: Props) => {
    const {t} = useTranslation();
    const [overview, setOverview] = useState<OverviewResult | undefined>();
    const [funnel, setFunnel] = useState<FunnelResult[] | undefined>();
    const [timePerStage, setTimePerStage] = useState<TimePerStageResult[] | undefined>();
    const [validationFailures, setValidationFailures] = useState<ValidationFailureResult[] | undefined>();
    const [versions, setVersions] = useState<VersionLabel[]>([]);
    const [loading, setLoading] = useState(false);

    const analyticsFilters = useMemo(() => toAnalyticsFilters(filters), [filters]);
    const sessionId = filters?.sessionId?.trim() || '';

    useEffect(() => {
        setAvailableFilters(AVAILABLE_FILTERS);
    }, [setAvailableFilters]);

    const fetchAll = useCallback(async () => {
        if (!analyticsFilters) {
            return;
        }
        setLoading(true);
        onToggleLoading(true);
        try {
            const [ov, fn, tps, vf, checksums] = await Promise.all([
                preSessionFlowAnalyticsApi.getOverview(analyticsFilters),
                preSessionFlowAnalyticsApi.getFunnel(analyticsFilters),
                preSessionFlowAnalyticsApi.getTimePerStage(analyticsFilters),
                preSessionFlowAnalyticsApi.getValidationFailures(analyticsFilters),
                preSessionFlowAnalyticsApi.getChecksumsInRange(analyticsFilters),
            ]);
            setOverview(ov);
            setFunnel(fn);
            setTimePerStage(tps);
            setValidationFailures(vf);
            // Resolve step-name labels via /versions bulk endpoint. Frontend picks the latest
            // version per stable stepId (see PreSessionFlowFunnelChart.buildLabelMap).
            if (checksums.length > 0) {
                const labels = await preSessionFlowAnalyticsApi.getVersionsLabels(checksums);
                setVersions(labels);
            } else {
                setVersions([]);
            }
        } finally {
            setLoading(false);
            onToggleLoading(false);
        }
    }, [analyticsFilters, onToggleLoading]);

    useEffect(() => {
        void fetchAll();
    }, [fetchAll]);

    const isLoading = loading || parentLoading;

    if (sessionId) {
        return (
            <div>
                <h2 className='tw-ts-h5-bold tw-mb-4'>
                    {t(T_KEYS.RUN_DETAIL.TITLE)}
                    {': '}
                    {sessionId}
                </h2>
                <PreSessionFlowRunDetail sessionId={sessionId} />
            </div>
        );
    }

    return (
        <div className='tw-flex tw-flex-col tw-gap-6'>
            <PreSessionFlowOverview data={overview} isLoading={isLoading} />
            <PreSessionFlowFunnelChart data={funnel} versions={versions} isLoading={isLoading} />
            <PreSessionFlowTimePerStage data={timePerStage} versions={versions} isLoading={isLoading} />
            <PreSessionFlowValidationFailures
                data={validationFailures}
                versions={versions}
                isLoading={isLoading}
            />
        </div>
    );
};
